// Sorting for the staff dashboard tables. Plain JavaScript so node can test it without a browser.
//
// A table cell is sorted by the value behind it, not the words on screen: dates by time, numbers (and numbers that arrived as
// text, like a bid price of "180.50") by size, true after false, and words alphabetically with "item 2" before "item 10".
// A list is sorted by its first item. Anything empty ("–", null, "" or an empty list) always goes last, whichever way round.

const words = new Intl.Collator('en-GB', { numeric: true, sensitivity: 'base' });
const plainNumber = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;
const isoDate = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(Z|[+-]\d{2}(?::?\d{2})?)?)?$/;
const dashes = new Set(['', '–', '—', '-']);

// What a value is compared as: false/true, then numbers and times, then words. Values of different kinds never compare as equal.
const BOOLEAN = 0;
const NUMBER = 1;
const WORDS = 2;

/** The time in an ISO date or timestamp ("2026-10-14", "2026-10-14T13:05:00Z", "2026-10-14 13:05:00.5+00"), or NaN when it isn't one. */
function isoTime(text) {
  const match = isoDate.exec(text);
  if (!match) return Number.NaN;
  const [, day, clock, zone = ''] = match;
  if (!clock) return Date.parse(day);
  return Date.parse(`${day}T${clock}${/^[+-]\d{2}$/.test(zone) ? `${zone}:00` : zone}`);
}

/** `{ kind, value }` ready to compare, or null when the value is empty. */
function normalise(value) {
  if (Array.isArray(value)) {
    for (const item of value) {
      const first = normalise(item);
      if (first) return first;
    }
    return null;
  }
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return { kind: BOOLEAN, value: value ? 1 : 0 };
  if (typeof value === 'number' || typeof value === 'bigint') {
    const number = Number(value);
    return Number.isFinite(number) ? { kind: NUMBER, value: number } : null;
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : { kind: NUMBER, value: time };
  }
  const text = String(value).trim();
  if (dashes.has(text)) return null;
  if (plainNumber.test(text)) return { kind: NUMBER, value: Number(text) };
  const time = isoTime(text);
  if (!Number.isNaN(time)) return { kind: NUMBER, value: time };
  return { kind: WORDS, value: text };
}

function compareNormalised(a, b) {
  if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
  if (a.kind === WORDS) return Math.sign(words.compare(a.value, b.value));
  return Math.sign(a.value - b.value);
}

/** True for null, undefined, "", "–", whitespace, NaN and a list with nothing in it. */
export function isEmpty(value) {
  return normalise(value) === null;
}

/**
 * Ascending order of two values: negative when `a` comes first, positive when `b` does, 0 when they tie.
 * An empty value comes after every value that has something in it.
 */
export function compareValues(a, b) {
  const first = normalise(a);
  const second = normalise(b);
  if (!first || !second) return (first ? 0 : 1) - (second ? 0 : 1);
  return compareNormalised(first, second);
}

/**
 * A new array of `rows` ordered by `valueOf(row)`, `direction` being 'asc' or 'desc'. Rows that tie keep the order they came in
 * (also when descending), and rows with an empty value are put last either way.
 */
export function sortRows(rows, valueOf, direction = 'asc') {
  const sign = direction === 'desc' ? -1 : 1;
  const filled = [];
  const empty = [];
  (rows || []).forEach((row, index) => {
    const key = normalise(valueOf(row));
    (key ? filled : empty).push({ row, key, index });
  });
  filled.sort((a, b) => sign * compareNormalised(a.key, b.key) || a.index - b.index);
  return [...filled, ...empty].map((item) => item.row);
}

/**
 * The direction a click on column `key` should sort in. `current` is what the table is sorted by now, `{ key, direction }`
 * (or null for the order the server sent). Clicking the same column again reverses it; a new column starts at `first`.
 */
export function nextDirection(current, key, first = 'asc') {
  if (current && current.key === key) return current.direction === 'desc' ? 'asc' : 'desc';
  return first === 'desc' ? 'desc' : 'asc';
}

/** `label(value)`, or null when `value` is empty, so a missing value sorts last instead of as its placeholder ("Never", "Not given"). */
export function labelOf(value, label) {
  return isEmpty(value) ? null : label(value);
}
